// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs';
import { getPublicEnv, PublicEnvKeys } from '@unifyapps/network/utils/PublicEnv/getPublicEnv';

const integrations = [Sentry.extraErrorDataIntegration()];

// For external assets sentry is not able to resolve the source maps
if (process.env.NEXT_CDN_PATH) {
  integrations.push(
    Sentry.rewriteFramesIntegration({
      iteratee: (frame) => {
        if (frame.filename) {
          // Decode the URI to handle encoded characters
          frame.filename = decodeURI(frame.filename);
          // Replace "app:///" with the correct path that matches your hosted source maps
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- NEXT_CDN_PATH is defined
          frame.filename = frame.filename.replace('app://', process.env.NEXT_CDN_PATH!);
        }
        return frame;
      },
    }),
  );
}

if (process.env.NODE_ENV === 'production' && getPublicEnv(PublicEnvKeys.SENTRY_ENABLED, false)) {
  Sentry.init({
    enabled: true,
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    initialScope: {
      tags: {
        branch: getPublicEnv(PublicEnvKeys.BRANCH_NAME, ''),
      },
    },
    allowUrls:
      typeof window === 'undefined'
        ? [/https?:\/\/((cdn|assets)\.)?unifyapps\.com/]
        : [window.location.origin, /https?:\/\/((cdn|assets)\.)?unifyapps\.com/],
    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    integrations,
    ignoreErrors: [
      'TypeError: Failed to fetch', // Ignore network errors
      'TypeError: network error', // Ignore network errors
      'TypeError: NetworkError when attempting to fetch resource.', // ref: https://github.com/sindresorhus/is-network-error/blob/7bbfa8be9482ce1427a21fbff60e3ee1650dd091/index.js#L8
      'Load failed',
      'ResizeObserver loop limit exceeded',
      /^AbortError/, // Ignore "expected" error from aborted fetch requests
      'The Internet connection appears to be offline.',
      'TypeError: Failed to parse body as FormData.', // thrown by security test, when sending binary data on any html doc
      /Uncaught NetworkError: Failed to execute 'importScripts' on 'WorkerGlobalScope'/,
      /groovy: @brackets/,
      '[UAFetchError]',
    ],
  });
}
